import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, SlidersHorizontal, X, ChevronDown, Star, ShoppingCart, Filter, Package, Heart } from "lucide-react";
import VariantSwatches from "@/components/VariantSwatches";
import { quickAddToCart, getCartCount } from "@/utils/cart";
import { toast } from "sonner";
import { expandByVariant1 } from "@/utils/variantExpand";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { ShopByPriceSection } from "@/pages/home/HomeSections";
import { useShopByPriceConfig } from "@/hooks/useShopByPriceConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SORT_OPTIONS = [
  { value: "relevance", label: "Best Match" },
  { value: "newest", label: "Newest First" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
  { value: "rating", label: "Highest Rated" },
];

function fmt(n) {
  if (!n && n !== 0) return "0";
  return Number(n).toLocaleString("en-IN");
}

function pct(mrp, price) {
  if (!mrp || mrp <= price) return 0;
  return Math.round(((mrp - price) / mrp) * 100);
}

function RatingStars({ rating = 0, count }) {
  return (
    <div className="flex items-center gap-1">
      <div className="flex items-center bg-green-600 text-white text-[11px] font-bold px-1.5 py-0.5 rounded gap-0.5">
        <span>{rating.toFixed(1)}</span>
        <Star className="w-2.5 h-2.5 fill-white" />
      </div>
      {count > 0 && <span className="text-xs text-slate-400">{count >= 1000 ? `${(count / 1000).toFixed(0)}k` : count}</span>}
    </div>
  );
}

function ProductCard({ product }) {
  const navigate = useNavigate();
  const price = Number(product.deal_price_incl_gst) || 0;
  const mrp = Number(product.mrp) || price;
  const discount = pct(mrp, price);
  const trackId = product._baseId || product.id;
  const rating = Number(product.star_rating) || 0;
  const reviews = Number(product.num_reviews) || 0;
  const [wishlisted, setWishlisted] = useState(() => {
    try { return JSON.parse(localStorage.getItem("it_wishlist") || "[]").includes(trackId); } catch { return false; }
  });

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    quickAddToCart(product);
    window.dispatchEvent(new Event("it_cart_updated"));
    toast.success("Added to cart");
  };

  const handleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const list = (() => { try { return JSON.parse(localStorage.getItem("it_wishlist") || "[]"); } catch { return []; } })();
    const next = list.includes(trackId) ? list.filter(id => id !== trackId) : [...list, trackId];
    localStorage.setItem("it_wishlist", JSON.stringify(next));
    window.dispatchEvent(new Event("wishlist-updated"));
    const added = !list.includes(trackId);
    setWishlisted(added);
    toast.success(added ? "Added to wishlist" : "Removed from wishlist");
  };

  // Build link with variant pre-selection
  const productLink = product._variantExpanded
    ? `/products/${product._baseSlug}?${product._variantKey.toLowerCase()}=${encodeURIComponent(product._variantValue)}`
    : `/products/${product.url_slug || product.id}`;

  return (
    <Link
      to={productLink}
      data-testid={`search-product-card-${trackId}`}
      className="group bg-white rounded-xl border border-slate-100 overflow-hidden hover:shadow-md hover:border-slate-200 transition-all duration-200 flex flex-col"
    >
      <div className="relative aspect-square overflow-hidden bg-slate-50">
        {product.main_image ? (
          <img
            src={product.main_image}
            alt={product.item_name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-10 h-10 text-slate-200" />
          </div>
        )}
        {discount >= 5 && (
          <span className="absolute top-2 left-2 bg-orange-500 text-white text-[11px] font-bold px-1.5 py-0.5 rounded">
            {discount}% off
          </span>
        )}
        {/* Variant label badge */}
        {product._variantExpanded && product._variantValue && (
          <span className="absolute bottom-2 left-2 bg-black/60 text-white text-[10px] font-medium px-2 py-0.5 rounded-full max-w-[80%] truncate">
            {product._variantValue}
          </span>
        )}
        {/* Wishlist heart */}
        <button
          onClick={handleWishlist}
          data-testid={`search-wishlist-btn-${trackId}`}
          className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center shadow-sm transition-all opacity-0 group-hover:opacity-100 ${
            wishlisted ? "bg-red-50 opacity-100" : "bg-white"
          }`}
        >
          <Heart className={`w-3.5 h-3.5 ${wishlisted ? "fill-red-500 text-red-500" : "text-slate-400"}`} />
        </button>
      </div>
      <div className="p-3 flex flex-col gap-1.5 flex-1">
        <p className="text-xs text-slate-500 font-medium truncate">{product.brand_name}</p>
        <h3 className="text-sm font-semibold text-slate-800 line-clamp-2 leading-snug">{product.item_name}</h3>
        {rating > 0 && <RatingStars rating={rating} count={reviews} />}
        {!product._variantExpanded && <VariantSwatches product={product} max={4} />}
        <div className="mt-auto pt-1">
          <div className="flex items-baseline gap-1.5">
            <span className="text-base font-black text-slate-900">₹{fmt(price)}</span>
            {mrp > price && (
              <span className="text-xs text-slate-400 line-through">₹{fmt(mrp)}</span>
            )}
          </div>
        </div>
        <button
          onClick={handleAddToCart}
          data-testid={`search-add-cart-${trackId}`}
          className="mt-1 w-full flex items-center justify-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold py-2 rounded-lg transition-colors"
        >
          <ShoppingCart className="w-3.5 h-3.5" />
          Add to Cart
        </button>
      </div>
    </Link>
  );
}

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [sort, setSort] = useState("relevance");
  const [showFilters, setShowFilters] = useState(false);
  // Price filter can come from the URL (Shop by Price banners link to /search?maxPrice=X)
  const urlMin = searchParams.get("minPrice") || "";
  const urlMax = searchParams.get("maxPrice") || "";
  const hasUrlPrice = !!(urlMin || urlMax);
  const [priceRange, setPriceRange] = useState({ min: urlMin, max: urlMax });
  const [searchInput, setSearchInput] = useState("");
  const navigate = useNavigate();
  const loaderRef = useRef(null);
  const recSentinelRef = useRef(null); // kept for layout ref
  const shopByPriceConfig = useShopByPriceConfig();

  const q = searchParams.get("q") || "";

  useEffect(() => {
    setSearchInput(q);
  }, [q]);

  const fetchProducts = useCallback(async (pageNum = 1, append = false, range = priceRange) => {
    if (!q && !range.min && !range.max) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        search: q,
        sort,
        page: pageNum,
        limit: 24,
        paginated: true,
      });
      if (range.min) params.set("min_price", range.min);
      if (range.max) params.set("max_price", range.max);

      const res = await axios.get(`${API}/products/public?${params}`);
      const data = res.data;
      if (append) {
        setProducts(prev => [...prev, ...data.products]);
      } else {
        setProducts(data.products);
        setTotal(data.total);
      }
      setHasMore(data.has_more);
    } catch (e) {
      toast.error("Failed to load search results");
    } finally {
      setLoading(false);
    }
  }, [q, sort, priceRange]);

  // Reset and fetch on query, sort or URL price change. When the URL price changes
  // (e.g. clicking another Shop by Price banner while already on /search), use it directly.
  const urlPriceRef = useRef(`${urlMin}|${urlMax}`);
  useEffect(() => {
    let range = priceRange;
    if (urlPriceRef.current !== `${urlMin}|${urlMax}`) {
      urlPriceRef.current = `${urlMin}|${urlMax}`;
      range = { min: urlMin, max: urlMax };
      setPriceRange(range);
    }
    setPage(1);
    setProducts([]);
    fetchProducts(1, false, range);
  }, [q, sort, urlMin, urlMax]);

  // Load more
  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchProducts(nextPage, true);
  };

  // Intersection observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, page]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      setSearchParams({ q: searchInput.trim() });
    }
  };

  const applyFilters = () => {
    setPage(1);
    setProducts([]);
    fetchProducts(1, false);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setPriceRange({ min: "", max: "" });
    setPage(1);
    setProducts([]);
  };

  const hasActiveFilters = priceRange.min || priceRange.max;

  return (
    <div className="min-h-screen bg-[#f5f7fa]">
      <SiteHeader
        defaultSearch={searchInput}
        showNav={false}
      />

      <div className="max-w-[1800px] mx-auto px-3 sm:px-4 py-4">
        {/* Results Meta */}
        <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
          <div data-testid="search-results-count">
            {q || hasUrlPrice ? (
              <p className="text-sm text-slate-600">
                {loading && products.length === 0 ? (
                  "Searching..."
                ) : (
                  <>
                    Showing <strong>{products.length}</strong>
                    {total > products.length && <> of <strong>{total}</strong></>}
                    {q ? (
                      <>{" "}results for{" "}<strong className="text-slate-900">"{q}"</strong></>
                    ) : (
                      <>{" "}products{urlMin && <> from <strong className="text-slate-900">₹{urlMin}</strong></>}{urlMax && <> under <strong className="text-slate-900">₹{urlMax}</strong></>}</>
                    )}
                  </>
                )}
              </p>
            ) : (
              <p className="text-sm text-slate-500">Enter a search term to find products</p>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Filter toggle */}
            <button
              onClick={() => setShowFilters(v => !v)}
              data-testid="search-filter-btn"
              className={`flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
                showFilters || hasActiveFilters
                  ? "border-orange-500 text-orange-600 bg-orange-50"
                  : "border-slate-300 text-slate-700 hover:border-slate-400"
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
              {hasActiveFilters && <span className="w-2 h-2 bg-orange-500 rounded-full" />}
            </button>

            {/* Sort */}
            <div className="relative">
              <div className="flex items-center gap-1.5 border border-slate-300 rounded-lg px-3 py-1.5 text-sm text-slate-700 cursor-pointer hover:border-slate-400 transition-colors">
                <SlidersHorizontal className="w-3.5 h-3.5 text-slate-400" />
                <select
                  value={sort}
                  onChange={e => setSort(e.target.value)}
                  data-testid="search-sort-select"
                  className="bg-transparent focus:outline-none text-sm font-semibold cursor-pointer"
                >
                  {SORT_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-5 shadow-sm" data-testid="search-filter-panel">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-slate-800 text-sm">Filters</h3>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="text-xs text-orange-500 font-semibold hover:text-orange-600">
                  Clear All
                </button>
              )}
            </div>
            <div className="flex items-end gap-4 flex-wrap">
              <div className="space-y-1">
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">Price Range (₹)</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={priceRange.min}
                    onChange={e => setPriceRange(p => ({ ...p, min: e.target.value }))}
                    data-testid="search-price-min"
                    className="w-24 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-orange-400"
                  />
                  <span className="text-slate-400 text-sm">–</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={priceRange.max}
                    onChange={e => setPriceRange(p => ({ ...p, max: e.target.value }))}
                    data-testid="search-price-max"
                    className="w-24 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-orange-400"
                  />
                </div>
              </div>
              <button
                onClick={applyFilters}
                data-testid="search-apply-filters"
                className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold rounded-lg transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        )}

        {/* No Query State */}
        {!q && !hasUrlPrice && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <Search className="w-14 h-14 text-slate-200 mb-4" />
            <h2 className="text-xl font-black text-slate-700 mb-2">Search for anything</h2>
            <p className="text-slate-400 text-sm">Try searching for electronics, fashion, home decor and more</p>
          </div>
        )}

        {/* No Results */}
        {(q || hasUrlPrice) && !loading && products.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="search-no-results">
            <Package className="w-14 h-14 text-slate-200 mb-4" />
            <h2 className="text-xl font-black text-slate-700 mb-2">No results found</h2>
            <p className="text-slate-400 text-sm mb-5">
              {q ? <>We couldn't find any products matching "<strong>{q}</strong>"</> : "We couldn't find any products in this price range"}
            </p>
            <Link to="/" className="px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm rounded-xl transition-colors">
              Browse All Products
            </Link>
          </div>
        )}

        {/* Product Grid */}
        {products.length > 0 && (() => {
          const expanded = expandByVariant1(products);
          return (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="search-product-grid">
              {expanded.map(p => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          );
        })()}

        {/* Loading skeleton */}
        {loading && products.length === 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-100 overflow-hidden animate-pulse">
                <div className="aspect-square bg-slate-100" />
                <div className="p-3 space-y-2">
                  <div className="h-3 bg-slate-100 rounded w-1/2" />
                  <div className="h-4 bg-slate-100 rounded" />
                  <div className="h-4 bg-slate-100 rounded w-3/4" />
                  <div className="h-7 bg-slate-100 rounded mt-2" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Infinite scroll loader */}
        <div ref={loaderRef} className="py-6 flex items-center justify-center">
          {loading && products.length > 0 && (
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
              Loading more...
            </div>
          )}
        </div>

        {/* Shop by Price — shown after search results are exhausted */}
        {!hasMore && products.length > 0 && shopByPriceConfig && (
          <div className="mt-6" data-testid="search-shop-by-price">
            <ShopByPriceSection config={shopByPriceConfig} />
          </div>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
